import React from 'react';
import {
    BookOpen,
    Rocket,
    LifeBuoy,
    ShieldCheck,
    BellRing,
    Activity,
    ExternalLink,
    Clock3,
    FileText,
    CreditCard
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';

const HelpCenter = () => {
    const { theme } = useTheme();
    const navigate = useNavigate();

    const cardClass = theme === 'dark'
        ? 'bg-gray-800 border border-gray-700'
        : 'bg-white border border-gray-200';

    const mutedTextClass = theme === 'dark' ? 'text-gray-400' : 'text-gray-600';
    const headingClass = theme === 'dark' ? 'text-white' : 'text-gray-900';

    return (
        <div className="max-w-7xl mx-auto space-y-6">
            <div className={`rounded-xl p-6 ${cardClass}`}>
                <h1 className={`text-2xl font-bold ${headingClass}`}>Help Center</h1>
                <p className={`text-sm mt-2 ${mutedTextClass}`}>
                    Find onboarding guidance, product documentation, support channels, release updates, and compliance information.
                </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                <button
                    onClick={() => navigate('/admin/dashboard')}
                    className={`text-left rounded-xl p-5 transition-colors hover:border-blue-400 ${cardClass}`}
                >
                    <Rocket className="w-5 h-5 text-blue-500 mb-3" />
                    <h2 className={`font-semibold ${headingClass}`}>Getting Started</h2>
                    <p className={`text-sm mt-1 ${mutedTextClass}`}>
                        Start from dashboard and use guided tours to onboard quickly.
                    </p>
                </button>

                <button
                    onClick={() => navigate('/admin/billing')}
                    className={`text-left rounded-xl p-5 transition-colors hover:border-purple-400 ${cardClass}`}
                >
                    <CreditCard className="w-5 h-5 text-purple-500 mb-3" />
                    <h2 className={`font-semibold ${headingClass}`}>Billing & Plans</h2>
                    <p className={`text-sm mt-1 ${mutedTextClass}`}>
                        Plan comparison, pricing, and upgrade guidance.
                    </p>
                </button>

                <a
                    href="mailto:support@oak-hr.com"
                    className={`rounded-xl p-5 transition-colors hover:border-emerald-400 ${cardClass}`}
                >
                    <LifeBuoy className="w-5 h-5 text-emerald-500 mb-3" />
                    <h2 className={`font-semibold ${headingClass}`}>Contact Support</h2>
                    <p className={`text-sm mt-1 ${mutedTextClass}`}>
                        Email support for setup, troubleshooting, and account assistance.
                    </p>
                </a>

                <a
                    href="#system-status"
                    className={`rounded-xl p-5 transition-colors hover:border-amber-400 ${cardClass}`}
                >
                    <Activity className="w-5 h-5 text-amber-500 mb-3" />
                    <h2 className={`font-semibold ${headingClass}`}>System Status</h2>
                    <p className={`text-sm mt-1 ${mutedTextClass}`}>
                        Check uptime, incidents, and scheduled maintenance updates.
                    </p>
                </a>
            </div>

            <section id="documentation" className={`rounded-xl p-6 ${cardClass}`}>
                <div className="flex items-center gap-2">
                    <BookOpen className="w-5 h-5 text-blue-500" />
                    <h2 className={`text-lg font-semibold ${headingClass}`}>Documentation</h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                    <div className="rounded-lg p-4 bg-blue-50/60 dark:bg-gray-700/40">
                        <p className={`font-medium ${headingClass}`}>Admin Setup</p>
                        <p className={`text-sm mt-1 ${mutedTextClass}`}>Organization structure, policies, and employee onboarding setup.</p>
                    </div>
                    <div className="rounded-lg p-4 bg-blue-50/60 dark:bg-gray-700/40">
                        <p className={`font-medium ${headingClass}`}>Operations Guides</p>
                        <p className={`text-sm mt-1 ${mutedTextClass}`}>Attendance, approvals, payroll, and document workflows.</p>
                    </div>
                    <div className="rounded-lg p-4 bg-blue-50/60 dark:bg-gray-700/40">
                        <p className={`font-medium ${headingClass}`}>Team Adoption</p>
                        <p className={`text-sm mt-1 ${mutedTextClass}`}>Rollout checklist, permissions, and launch best practices.</p>
                    </div>
                </div>
            </section>

            <section id="product-updates" className={`rounded-xl p-6 ${cardClass}`}>
                <div className="flex items-center gap-2">
                    <BellRing className="w-5 h-5 text-purple-500" />
                    <h2 className={`text-lg font-semibold ${headingClass}`}>Product Updates</h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    <div className="rounded-lg p-4 bg-purple-50/60 dark:bg-gray-700/40">
                        <p className={`font-medium ${headingClass}`}>Release Notes</p>
                        <p className={`text-sm mt-1 ${mutedTextClass}`}>Track new features, fixes, and improvements shipped to the platform.</p>
                    </div>
                    <div className="rounded-lg p-4 bg-purple-50/60 dark:bg-gray-700/40">
                        <p className={`font-medium ${headingClass}`}>Roadmap Highlights</p>
                        <p className={`text-sm mt-1 ${mutedTextClass}`}>See what is planned next for admin, manager, and employee experiences.</p>
                    </div>
                </div>
            </section>

            <section id="system-status" className={`rounded-xl p-6 ${cardClass}`}>
                <div className="flex items-center gap-2">
                    <Activity className="w-5 h-5 text-emerald-500" />
                    <h2 className={`text-lg font-semibold ${headingClass}`}>System Status</h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    <div className="rounded-lg p-4 bg-emerald-50/60 dark:bg-gray-700/40">
                        <p className={`font-medium ${headingClass}`}>Service Health</p>
                        <p className={`text-sm mt-1 ${mutedTextClass}`}>Monitor availability across authentication, employee management, and attendance services.</p>
                    </div>
                    <div className="rounded-lg p-4 bg-emerald-50/60 dark:bg-gray-700/40">
                        <p className={`font-medium ${headingClass}`}>Maintenance Windows</p>
                        <p className={`text-sm mt-1 ${mutedTextClass}`}>Planned upgrades and downtime notices will be posted here.</p>
                    </div>
                </div>
            </section>

            <section id="contact-support" className={`rounded-xl p-6 ${cardClass}`}>
                <div className="flex items-center gap-2">
                    <LifeBuoy className="w-5 h-5 text-amber-500" />
                    <h2 className={`text-lg font-semibold ${headingClass}`}>Support Channels</h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                    <a href="mailto:support@oak-hr.com" className="rounded-lg p-4 bg-amber-50/60 dark:bg-gray-700/40">
                        <p className={`font-medium ${headingClass}`}>Email Support</p>
                        <p className={`text-sm mt-1 ${mutedTextClass}`}>support@oak-hr.com</p>
                    </a>
                    <div className="rounded-lg p-4 bg-amber-50/60 dark:bg-gray-700/40">
                        <div className="flex items-center gap-1.5">
                            <Clock3 className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                            <p className={`font-medium ${headingClass}`}>Response SLA</p>
                        </div>
                        <p className={`text-sm mt-1 ${mutedTextClass}`}>Standard: within 1 business day.</p>
                    </div>
                    <a
                        href="mailto:sales@oak-hr.com?subject=Enterprise%20Support%20Inquiry"
                        className="rounded-lg p-4 bg-amber-50/60 dark:bg-gray-700/40"
                    >
                        <p className={`font-medium ${headingClass}`}>Enterprise Assistance</p>
                        <p className={`text-sm mt-1 ${mutedTextClass}`}>Priority onboarding and custom support channels.</p>
                    </a>
                </div>
            </section>

            <section id="legal" className={`rounded-xl p-6 ${cardClass}`}>
                <div className="flex items-center gap-2">
                    <ShieldCheck className="w-5 h-5 text-indigo-500" />
                    <h2 className={`text-lg font-semibold ${headingClass}`}>Legal & Compliance</h2>
                </div>
                <p className={`text-sm mt-2 ${mutedTextClass}`}>
                    Access the core legal documents and compliance resources used by procurement, IT security, and HR teams.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    <a href="/admin/privacy" className="rounded-lg p-4 bg-indigo-50/60 dark:bg-gray-700/40">
                        <div className="flex items-center justify-between">
                            <p className={`font-medium ${headingClass}`}>Privacy Policy</p>
                            <ExternalLink className={`w-4 h-4 ${mutedTextClass}`} />
                        </div>
                        <p className={`text-sm mt-1 ${mutedTextClass}`}>How personal data is collected, processed, retained, and protected.</p>
                    </a>
                    <a href="/admin/terms" className="rounded-lg p-4 bg-indigo-50/60 dark:bg-gray-700/40">
                        <div className="flex items-center justify-between">
                            <p className={`font-medium ${headingClass}`}>Terms of Service</p>
                            <ExternalLink className={`w-4 h-4 ${mutedTextClass}`} />
                        </div>
                        <p className={`text-sm mt-1 ${mutedTextClass}`}>Contractual terms, billing conditions, and acceptable use requirements.</p>
                    </a>
                </div>
                <div className="mt-4 rounded-lg p-4 bg-indigo-50/70 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800/40">
                    <p className={`text-xs ${mutedTextClass}`}>
                        Legal and compliance content is informational and does not constitute legal advice. For policy decisions, consult your legal counsel.
                    </p>
                </div>
            </section>
        </div>
    );
};

export default HelpCenter;
